import { Block } from '@contensis/html-canvas';
import { ContentType, Field } from 'contensis-core-api';
import { Entry, EntrySys, Node, Tag } from 'contensis-management-api';
import {
  DependenciesMeta,
  EntryDependenciesMeta,
  EntryDependencyMeta,
  EntryDependencyOrigin,
  ExistingDependenciesMeta,
  NodeDependenciesMeta,
  TagDependenciesMeta,
  TagDependencyMeta,
} from '~/models/EntryDependencies';
import ContensisRepository from '~/repositories/ContensisRepository';
import ContentTypeHelper from '~/util/content-type/ContentTypeHelper';
import { firstValueOf } from '~/util/object';
import { isArray, isNullOrUndefined, isString } from '~/util/typeguards';

/**
 * Use for every field in an entry, component or composer,
 * providing the field's configuration and value and a content type
 * helper so we can locate and check fields in any composers or components
 */
export class EntryFieldCheck {
  assetUris = new Map<string, Field>();
  entryIds: EntryDependencyMeta[] = [];
  nodeIds = new Map<string, Field>();
  tagIds: TagDependencyMeta[] = [];
  tagGroupIds = new Map<string, Field>();
  dependencyIds: { [id: string]: EntryDependencyOrigin[] } = {};

  total = 0;
  required = 0;

  get fieldAndAncestors() {
    return [...(this.parentFields || []), this.field];
  }

  get fieldIdPath() {
    return this.fieldAndAncestors
      .map(
        (f) => `${f.id}${this.context.field.assert.IsRepeatable(f) ? '[]' : ''}`
      )
      .join('.');
  }

  get hasDependencies() {
    return (
      this.entryIds.length > 0 ||
      this.assetUris.size > 0 ||
      this.nodeIds.size > 0 ||
      this.tagIds.length > 0
    );
  }

  get isAssetField() {
    return (
      this.context.field.assert.IsAsset(this.field) ||
      this.context.field.assert.IsImage(this.field)
    );
  }

  get isRequiredField() {
    return this.context.field.assert.validation.IsRequired(this.field);
  }

  /** Track origin of dependencies by their id for reporting later */
  #trackDependencyId = (key: string, originMeta: EntryDependencyOrigin) => {
    const existing = this.dependencyIds[key];
    if (existing) {
      existing.push(originMeta);
    } else {
      this.dependencyIds[key] = [originMeta];
    }
  };

  #addAssetUri = (uri?: string) => {
    const addUri = uri?.split('.')?.[0];
    if (addUri) this.assetUris.set(addUri, this.field);
  };

  #addEntryDependency = (entry: Entry) => {
    const { asset, entryTitle = '' } = (entry || {}) as {
      entryTitle?: string;
      asset?: Entry;
    };
    if (!entry?.sys && !entry?.asset?.sys) return;

    const sys = (asset?.sys || entry.sys) as EntrySys;

    // if (!this.entryIds.find(([, dependencyId]) => dependencyId === sys.id))
    this.entryIds.push({
      contentTypeId: sys.contentTypeId,
      dependencyLevel: this.dependencyLevel,
      entryId: sys.id,
      entryTitle,
      fieldIdPath: this.fieldIdPath,
      uri: sys.uri,
    });

    // If this is an asset or image field, track the uri for later resolution
    if (this.isAssetField && sys?.uri) this.#addAssetUri(sys.uri);

    const originMeta: EntryDependencyOrigin = {
      contentTypeId: this.entry.sys.contentTypeId || '',
      dependencyLevel: this.dependencyLevel,
      entryId: this.entry.sys.id || '',
      entryTitle: this.entry.entryTitle,
      fieldIdPath: this.fieldIdPath,
      type: this.isAssetField ? 'asset' : 'entry',
      uri: sys.uri,
    };
    this.#trackDependencyId(sys.id, originMeta);

    // Track the number of refereces to entries in required fields,
    // don't count assets as we will always resolve and migrate these
    if (this.isRequiredField && !this.context.field.assert.IsImage(this.field))
      this.required += 1;
    this.total += 1;
  };

  #addNodeDependency = ({ id, path, slug }: Partial<Node>) => {
    if (id) {
      this.nodeIds.set(id, this.field);
      const originMeta: EntryDependencyOrigin = {
        contentTypeId: this.entry.sys.contentTypeId || '',
        dependencyLevel: this.dependencyLevel,
        entryId: this.entry.sys.id || '',
        entryTitle: this.entry.entryTitle,
        fieldIdPath: this.fieldIdPath,
        type: 'node',
        uri: firstValueOf(path || slug),
      };
      this.#trackDependencyId(id, originMeta);
      this.total += 1;
    }
  };

  #addTagDependency = (tag: Partial<Tag>) => {
    const originMeta: EntryDependencyOrigin = {
      contentTypeId: this.entry.sys.contentTypeId || '',
      dependencyLevel: this.dependencyLevel,
      entryId: this.entry.sys.id || '',
      entryTitle: this.entry.entryTitle,
      fieldIdPath: this.fieldIdPath,
      type: 'tag',
    };
    if (isString(tag)) {
      // If tag is presented from a Delivery API entry, we can't resolve it here
      this.#trackDependencyId(tag, originMeta);
    } else if (tag.id) {
      this.tagIds.push({
        groupId: tag.groupId as string,
        tagId: tag.id,
        label: tag.label?.[
          this.context.project?.primaryLanguage as string
        ] as string,
        fieldIdPath: this.fieldIdPath,
      });
      // Add the tag group to the dependencies list resolving the id from the content type field
      // only if we have pushed a tagId
      this.tagGroupIds.set(
        this.context.resolve.tagGroupId(this.field.dataFormat),
        this.field
      );

      this.#trackDependencyId(tag.id, originMeta);
    }
    if (this.isRequiredField) this.required += 1;
    this.total += 1;
  };

  #handleDependentEntryField = () => {
    const { value } = this;

    // If dependency field's value contains multiple dependencies
    if (isArray(value))
      for (const innerValue of value) this.#addEntryDependency(innerValue);
    else if (value) this.#addEntryDependency(value);
  };

  #checkTagPickerField = () => {
    const { value } = this;

    // If dependency field's value contains multiple dependencies
    if (isArray(value))
      for (const innerValue of value) this.#addTagDependency(innerValue);
    else if (value) this.#addTagDependency(value);
  };

  #checkComponentFields = (componentId?: string, value?: any) => {
    const component = this.context.resolve.component(componentId);

    // Check every field inside component for dependencies
    for (const field of component?.fields || []) {
      // If component field's value contains multiple component items
      if (isArray(value)) {
        for (const inner of value)
          if (!isNullOrUndefined(inner))
            new EntryFieldCheck(
              inner[field.id],
              field,
              this.entry,
              this.context,
              this,
              this.dependencyLevel,
              this.fieldAndAncestors
            );
      } else {
        if (!isNullOrUndefined(value))
          new EntryFieldCheck(
            value[field.id],
            field,
            this.entry,
            this.context,
            this,
            this.dependencyLevel,
            this.fieldAndAncestors
          );
      }
    }
  };

  #checkComposerFields = (composerField: Field, composerItems: any[] = []) => {
    const fields = this.context.field.GetComposerFields(composerField);

    for (const field of fields) {
      // Filter composer values for this field
      const values = composerItems
        .filter((item) => item.type === field.id)
        .map((item) => item.value)
        .flat();

      // Check field inside composer for dependencies
      new EntryFieldCheck(
        values,
        field,
        this.entry,
        this.context,
        this,
        this.dependencyLevel,
        this.fieldAndAncestors
      );
      // if (composerFieldCheck.total > 0)
      //   this.total += composerFieldCheck.total;
    }
  };

  #checkCanvasBlocks = (canvasField: Field, blocks: Block[] = []) => {
    if (!isArray(blocks)) return;
    // Loop over canvas blocks
    for (const block of blocks) {
      // Recurse if value is array
      if (isArray(block.value)) {
        this.#checkCanvasBlocks(canvasField, block.value);
      } else if (block.type === '_inlineEntry') {
        this.#addEntryDependency(block.value as Entry);
      } else if (block.type === '_link') {
        const sys = block.properties?.link?.sys || ({} as EntrySys);
        //   - properties.link.sys.id / contentTypeId / language
        if (sys.dataFormat === 'entry' || sys.dataFormat === 'asset')
          this.#addEntryDependency({ sys });

        //   - properties.link.sys.node.id
        if ('node' in sys && sys.node)
          this.#addNodeDependency({ id: sys.node.id });

        // If uri
        //   - properties.link.sys.linkProperties.type === 'uri'
        //   - properties.link.sys.uri
      } else if (block.type === '_image') {
        const entry = block.value;
        if (entry) {
          this.#addEntryDependency(entry.asset as Entry);
          this.#addAssetUri(entry.asset?.sys?.uri);
        }
      } else if (block.type === '_component') {
        this.#checkComponentFields(block.properties?.component, block.value);
      }
    }
  };

  constructor(
    private value: any,
    private field: Field,
    private entry: Entry,
    private context: ContentTypeHelper,
    private dependencies: ExistingDependenciesMeta,
    public dependencyLevel: number,
    private parentFields?: Field[]
  ) {
    // do checks and merge straight away
    this.#checks();
    this.#merge();
  }

  /**
   * Do a standard set of field type assertions for a given field and value
   */
  #checks = () => {
    const { assert } = this.context.field;
    const { field, value } = this;

    // Entry-type field
    if (assert.IsEntry(field) || assert.IsAsset(field) || assert.IsImage(field))
      this.#handleDependentEntryField();

    // Component field
    if (assert.IsComponent(field))
      this.#checkComponentFields(field.dataFormat, value);

    // Composer field
    if (assert.IsComposer(field)) this.#checkComposerFields(field, value);

    // Canvas field
    if (assert.IsCanvas(field)) this.#checkCanvasBlocks(field, value);

    // Tag picker field
    if (assert.IsTagPicker(field)) this.#checkTagPickerField();
  };

  /**
   * Merge these field dependencies into other dependencies
   */
  #merge = () => {
    const { entryIds, assetUris, nodeIds, tagGroupIds, tagIds, dependencyIds } =
      this.dependencies;
    // merge in the ids from the field dependencies into the entry dependencies
    for (const d of this.entryIds)
      if (!entryIds.find((meta) => meta.entryId === d.entryId))
        entryIds.push(d);

    // merge any asset uris
    if (this.assetUris.size > 0)
      this.dependencies.assetUris = new Map([...assetUris, ...this.assetUris]);

    // merge any node ids found in canvas fields
    if (this.nodeIds.size > 0)
      this.dependencies.nodeIds = new Map([...nodeIds, ...this.nodeIds]);

    // merge any tag groups
    if (this.tagGroupIds.size > 0)
      this.dependencies.tagGroupIds = new Map([
        ...tagGroupIds,
        ...this.tagGroupIds,
      ]);

    // merge any tag ids
    for (const d of this.tagIds)
      if (!tagIds.find((meta) => meta.tagId === d.tagId)) tagIds.push(d);

    if (Object.keys(this.dependencyIds).length > 0) {
      for (const [key, value] of Object.entries(this.dependencyIds)) {
        const existing = dependencyIds[key];
        if (existing) {
          existing.push(...value);
        } else {
          dependencyIds[key] = [...value];
        }
      }
    }
    this.dependencies.required += this.required;
    this.dependencies.total += this.total;
  };
}

/**
 * Use for each entry providing the entry value and a content type
 * helper so we can resolve the content type and check every field
 */
export class EntryDependencyCheck {
  private contentType: ContentType;
  assetUris = new Map<string, Field>();
  entryIds: EntryDependencyMeta[] = [];
  nodeIds = new Map<string, Field>();
  tagIds: TagDependencyMeta[] = [];
  tagGroupIds = new Map<string, Field>();
  dependencyIds: { [id: string]: EntryDependencyOrigin[] } = {};

  // New index of entry fields and dependency metadata
  fields: { [fieldId: string]: EntryFieldCheck } = {};
  hasDependencies = false;
  total = 0;
  required = 0;
  requiredFields: { [fieldId: string]: EntryFieldCheck } = {};

  constructor(
    public entry: Entry,
    private context: ContentTypeHelper,
    private dependencies: DependenciesMeta,
    public dependencyLevel: number
  ) {
    this.contentType = context.resolve.contentType(
      entry.sys.contentTypeId
    ) as ContentType;

    this.#findDependentEntryIds();
    this.#merge();
  }

  #findDependentEntryIds = (entry: Entry = this.entry) => {
    const { contentType, context, dependencyLevel } = this;
    if (!contentType) return [];

    // Check the field for dependencies
    for (const field of contentType.fields || []) {
      const fieldDependencies = new EntryFieldCheck(
        entry[field.id],
        field,
        entry,
        context,
        this,
        dependencyLevel
      );
      if (fieldDependencies.hasDependencies) {
        this.fields[field.id] = fieldDependencies;
        if (fieldDependencies.isRequiredField) {
          this.requiredFields[field.id] = fieldDependencies;
        }
        this.hasDependencies = true;
      }
    }
    return this.entryIds;
  };

  /** Merge the dependencies found in the current entry into the job dependencies */
  #merge = (
    { entryIds, assetUris, nodeIds, tagGroupIds, tagIds, dependencyIds } = this
      .dependencies
  ) => {
    // merge in the ids from the entry dependencies into the job dependencies
    if (this.entryIds.length && this.entry.sys.id) {
      const existing = entryIds[this.entry.sys.id];
      if (existing) {
        existing.push(...this.entryIds);
      } else entryIds[this.entry.sys.id] = this.entryIds;
    }

    // merge any asset uris
    if (this.assetUris.size > 0)
      this.dependencies.assetUris = new Set([
        ...assetUris,
        ...this.assetUris.keys(),
      ]);

    // merge any node ids found in canvas fields
    if (this.nodeIds.size > 0) {
      const i = nodeIds.findIndex(([id]) => id === this.entry.sys.id);
      const nodesArray: [string][] = Array.from(this.nodeIds.keys()).map(
        (y) => [y]
      );
      if (i > -1) {
        nodeIds[i][1] = nodesArray.map((n) => [n[0], this.dependencyLevel]);
      } else
        nodeIds.push([
          `${this.entry.sys.id}`,
          nodesArray.map((n) => [n[0], this.dependencyLevel]),
        ]);
    }

    // merge any tag group ids
    if (this.tagGroupIds.size > 0)
      this.dependencies.tagGroupIds = new Set([
        ...tagGroupIds,
        ...this.tagGroupIds.keys(),
      ]);

    // merge any tag ids
    if (this.tagIds.length && this.entry.sys.id) {
      const existing = tagIds[this.entry.sys.id];
      if (existing) {
        existing.push(...this.tagIds);
      } else tagIds[this.entry.sys.id] = this.tagIds;
    }

    if (Object.keys(this.dependencyIds).length > 0) {
      for (const [key, value] of Object.entries(this.dependencyIds)) {
        const existing = dependencyIds[key];
        if (existing) {
          existing.push(...value);
        } else {
          dependencyIds[key] = [...value];
        }
      }
    }

    this.dependencies.total += this.total;
    this.dependencies.required += this.required;
  };
}

type EntryDependencyLevel = {
  /** Length is the number of unique entries found as unresolved dependencies at this level */
  length: number;
  /** Total is the sum of all references to linked content,
   * this may be higher than the number of dependencies if the
   * same content dependency is linked multiple times
   */
  total: number;
  /** Required is the sum of all references to linked content that
   * are in required fields
   */
  required: number;
} & {
  [entryId: Exclude<string, 'total' | 'length'>]: EntryDependencyCheck;
};

class EntriesDependencyService {
  // Maintain lists of uris and ids found during dependency checks
  // to refer to later
  assetUris = new Set<string>();
  entryIds: EntryDependenciesMeta = {};
  nodeIds: NodeDependenciesMeta[] = [];
  tagGroupIds = new Set<string>();
  tagIds: TagDependenciesMeta = {};
  dependencyIds: { [id: string]: EntryDependencyOrigin[] } = {};

  // New index of entry ids and dependency metadata partitioned by dependency level
  levels: EntryDependencyLevel[] = [];
  total = 0;
  required = 0;
  dependencyLevels: EntryDependencyLevel[] = [];
  requiredLevels: EntryDependencyLevel[] = [];
  cutoffLevels: EntryDependencyLevel[] = [];
  stopLevel?: number | undefined;

  constructor(public target: ContensisRepository) {}
  /**
   * Find all dependencies in the given entry and merge the found dependencies
   * in with all the other dependencies for this job instance
   * @param entry
   * @returns metadata of dependent entries for this entry
   */
  FindEntryDependencies = (entry: Entry, level = 1) => {
    const dependencyLevel = level - 1;

    // Tee up a new dependencies object for this level if we don't have one already
    if (!this.levels[level - 1]) {
      this.levels[level - 1] = {
        length: 0,
        required: 0,
        total: 0,
      } as EntryDependencyLevel;
      this.dependencyLevels[level - 1] = { ...this.levels[level - 1] };
      this.requiredLevels[level - 1] = { ...this.levels[level - 1] };
      this.cutoffLevels[level - 1] = { ...this.levels[level - 1] };
    }

    const currentLevel = this.levels[level - 1];
    const levelDependencies = this.dependencyLevels[level - 1];
    const requiredLevel = this.requiredLevels[level - 1];

    // Check have we already found dependencies for this entry at any level
    if (entry.sys?.id) {
      const entryDependencies = this.levels.find(
        (l) => l?.[entry.sys.id || '']
      )?.[entry.sys.id];
      if (entryDependencies) {
        // If we have, we can reuse the existing dependencies and not count them again
        currentLevel[entry.sys.id] = entryDependencies;
        currentLevel.total += entryDependencies.total;
        currentLevel.length += 1;
        return entryDependencies.entryIds;
      }
    }

    // Create a new dependencies object to collect dependencies for this entry
    const dependencies = new EntryDependencyCheck(
      entry,
      this.target.context,
      this,
      dependencyLevel
    );

    // Add the dependencies for this entry into the level index
    if (entry.sys?.id) {
      currentLevel[entry.sys.id] = dependencies;
      currentLevel.total += dependencies.total;
      currentLevel.required += dependencies.required;
      currentLevel.length += 1;

      // If we have found dependencies for this entry, add them to the dependency level index
      if (dependencies.total) {
        levelDependencies[entry.sys.id] = dependencies;
        levelDependencies.total += dependencies.total;
        levelDependencies.required += dependencies.required;
        levelDependencies.length += 1;
      }

      // If we have found required field dependencies for this entry, add them to the required level index
      if (
        dependencies.required &&
        Object.keys(dependencies.requiredFields).length
      ) {
        requiredLevel[entry.sys.id] = dependencies;
        requiredLevel.total += dependencies.total;
        requiredLevel.required += dependencies.required;
        requiredLevel.length += 1;

        // Add to the cutoff level if required fields are present
        // in the previous required level
        if (level > 1) {
          // Loop all entries in the previous required level
          // filtering out any that don't have required fields
          for (const previousLevelRequiredEntry of Object.values(
            this.requiredLevels[level - 2]
          ).filter(
            (e) => typeof e !== 'number' && Object.keys(e.requiredFields).length
          ) as EntryDependencyCheck[]) {
            // Loop all required fields in the previous level's entry
            // and check if the current entry id is mentioned in any of them,
            // If present in a required field, add the current entry to the cutoff level
            // so we can identify when we could stop processing dependencies
            for (const requiredField of Object.values(
              previousLevelRequiredEntry.requiredFields
            )) {
              if (
                requiredField.entryIds.find(
                  ({ entryId }) => entryId === entry.sys?.id
                )
              ) {
                this.cutoffLevels[level - 1][entry.sys.id] = dependencies;
                this.cutoffLevels[level - 1].total += dependencies.total;
                this.cutoffLevels[level - 1].required += dependencies.required;
                this.cutoffLevels[level - 1].length += 1;
                break;
              }
            }
          }
        }
      }
    }

    return dependencies.entryIds;
  };

  /** Get the levels from where this dependency was found,
   * the first level is the farthest level it was found at
   * @param entryId
   * @returns array of levels where this entry id was found as a dependency
   */
  getEntryDependencyLevels(entryId: string): number[] | undefined {
    const levels: number[] = [];

    for (const [level, entries] of Object.entries(this.levels)) {
      // locate the parent entry id in the entryIds list
      const entryIds = Object.entries(this.entryIds).filter(
        ([, dependencies]) =>
          dependencies.filter((meta) => meta.entryId === entryId).length > 0
      );
      // iterate the found parent entries and add the level to an array
      // as it may be found in multiple entries at different levels
      for (const [parentId] of entryIds) {
        if (entries[parentId]) {
          levels.push(Number(level));
        }
      }
    }
    // sort any found levels in descending order and return
    levels.sort((a, b) => b - a);
    return levels.length > 0 ? levels : undefined;
  }
}

export default EntriesDependencyService;
